import React from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  Home as HomeIcon,
  Logout,
  Mail,
  PersonOutline,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { APP_NAME } from '../constants/app';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title = APP_NAME }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const { notificationCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const openProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '';

  const onNotifications = location.pathname.startsWith('/notifications');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ minHeight: { xs: 64, sm: 80 }, gap: { xs: 1, sm: 1.5 } }}>
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Logo size={isMobile ? 'small' : 'medium'} />
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && <LanguageSwitcher color="primary" />}

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

              <IconButton
                color={onNotifications ? 'primary' : 'default'}
                onClick={() => navigate('/notifications')}
                title={t('layout.notifications')}
                sx={{ bgcolor: onNotifications ? alpha(theme.palette.primary.main, 0.08) : 'transparent' }}
              >
                <Badge color="error" invisible={notificationCount === 0} variant="dot">
                  <Mail />
                </Badge>
              </IconButton>
              <IconButton
                size="large"
                aria-label={t('layout.accountMenuAria')}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={openProfileMenu}
                color="inherit"
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 800,
                  }}
                >
                  {initials || <AccountCircle />}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 240,
                    borderRadius: 2,
                    overflow: 'hidden',
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.75 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                {isMobile && (
                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <LanguageSwitcher color="primary" />
                  </Box>
                )}
                <Divider />
                <MenuItem onClick={() => { handleClose(); navigate('/'); }}>
                  <HomeIcon fontSize="small" sx={{ mr: 1.5 }} />
                  {t('layout.myHouses')}
                </MenuItem>
                <MenuItem onClick={() => { handleClose(); navigate('/notifications'); }}>
                  <Mail fontSize="small" sx={{ mr: 1.5 }} />
                  {t('layout.notifications')}
                </MenuItem>
                <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
                  <PersonOutline fontSize="small" sx={{ mr: 1.5 }} />
                  {t('layout.profile')}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <Logout fontSize="small" sx={{ mr: 1.5 }} />
                  {t('common.actions.logout')}
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: 3, sm: 4 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
